import React, { Component } from 'react'
import { Col, Row, Modal } from 'react-bootstrap';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Paper, Button } from "@mui/material";
import { sumMoney, formatAccounting, formatPaddedDate, formatPaddedWeek, getISOWeekDates, normalizeWeekCode } from '../../helper';
import SectionHeader from '../../Pages/admin/shared/SectionHeader';
import { NotApprovedRange } from '../../Pages/admin/shared/NotApprovedTag';

/* Profit & Loss — Summary: one row per shop for the whole selected range.
 *
 * Laid out after the legacy report the user supplied (phone-clinic/reportLayout/
 * profitnlossSummary.html, T1.36, 2026-10-26), "exactly as the file" by the user's choice: ONE
 * table of 12 columns — Shop Name, Cash, Card, Days, Total Sales, Rent, K&N, Delivery, NDR,
 * Other, Total Exp, PROF & LOSS — and a Total row summing only Cash and Card. The display-only
 * 25% columns (2026-10-14) are not shown in this view; By Week / By Shop keep theirs.
 *
 * Figures are index.js's, unchanged: Total Sales is still Cash + Card + Others (Others is 0 in
 * every week, so this equals legacy's Cash + Card). Days follows legacy (sale days with
 * cash + card > 0). Other opens the lines it is made of.
 */

const HEAD_CELL = { background: '#f1f1f1', color: '#111827', fontWeight: 600, whiteSpace: 'normal' };
/* Twelve columns must fit the page without a sideways scroll (project rule), so this table runs a
   little tighter than the default — `report-table--tight` in index.css (12px, 8px side padding) —
   with wrapping headers and shop names. */
const NUM = { textAlign: 'right', whiteSpace: 'nowrap' };
const FOOT = { fontWeight: 800, color: '#111827', background: '#e7e7e7' };

// The Other figure as a button: keyboard-focusable, looks like a link, prints as plain text.
const LINK_BUTTON = {
    background: 'none', border: 0, padding: 0, font: 'inherit', color: '#1d4ed8',
    textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer',
};

// "28 Sep 2026 - 25 Oct 2026": Monday of the From week to Sunday of the To week.
function rangeLabel(fromWeek, toWeek) {
    if (!fromWeek || !toWeek) return '';
    const { start } = getISOWeekDates(normalizeWeekCode(fromWeek));
    const { end } = getISOWeekDates(normalizeWeekCode(toWeek));
    return `${formatPaddedDate(start)} - ${formatPaddedDate(end)}`;
}

class Summary extends Component {
    constructor(props) {
        super(props);
        this.state = { otherFor: null };
    }

    renderOtherModal() {
        const shop = this.state.otherFor;
        const lines = (shop && shop.otherLines) || [];
        return (
            <Modal show={!!shop} onHide={() => this.setState({ otherFor: null })} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Other expenses — {shop ? shop.ShopName : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table size="small" className="report-table pc-shrink-table" aria-label="other expense lines">
                        <TableHead>
                            <TableRow>
                                <TableCell style={HEAD_CELL}>Week</TableCell>
                                <TableCell style={HEAD_CELL}>Description</TableCell>
                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.length === 0 && (
                                <TableRow><TableCell colSpan={3} align="center" style={{ color: '#64748b' }}>No other expenses in this range.</TableCell></TableRow>
                            )}
                            {lines.map((l, i) => (
                                <TableRow key={i} className="table-rows">
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>{formatPaddedWeek(l.week)}</TableCell>
                                    <TableCell>{l.description}</TableCell>
                                    <TableCell style={NUM}>{formatAccounting(l.amount)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={2} style={FOOT}>Total</TableCell>
                                <TableCell style={{ ...FOOT, ...NUM }}>{formatAccounting(shop ? shop.other : 0)}</TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outlined" size="small" onClick={() => this.setState({ otherFor: null })}>Close</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { ReportDetails } = this.props;
        const rows = (ReportDetails && ReportDetails.reportActData) || [];
        const money = (v) => formatAccounting(v);

        return (
            <section>
                <SectionHeader title={<>{rangeLabel(ReportDetails?.['Day1'], ReportDetails?.['Day2'])}<NotApprovedRange weeks={this.props.UnapprovedWeeks} /></>} />

                {rows.length === 0 && (
                    <Row><Col md={12}>
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                            No approved weekly sales in this range.
                        </div>
                    </Col></Row>
                )}

                {rows.length > 0 && (
                    <Row>
                        <Col md={12} >
                            <Paper>
                                <TableContainer className="report-table-container" >
                                    <Table className="report-table report-table--tight pc-shrink-table" size="small" aria-label="profit and loss summary">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={HEAD_CELL}>Shop Name</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Cash</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Card</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Days</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Total Sales</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Rent</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>K&N</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Delivery</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>NDR</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Other</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>Total Exp</TableCell>
                                                <TableCell style={{ ...HEAD_CELL, ...NUM }}>PROF & LOSS</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map((x, idx) => (
                                                <TableRow className="table-rows" key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} >
                                                    <TableCell className="report-label-cell" style={{ whiteSpace: 'normal' }}>{x.ShopName}</TableCell>
                                                    <TableCell style={NUM}>{money(x.Cash)}</TableCell>
                                                    <TableCell style={NUM}>{money(x.Card)}</TableCell>
                                                    <TableCell style={NUM}>{x.Days || 0}</TableCell>
                                                    <TableCell style={NUM}>{money(x.TotalSale)}</TableCell>
                                                    <TableCell style={NUM}>{money(x.rentCharges)}</TableCell>
                                                    <TableCell style={NUM}>{money(x.KnN)}</TableCell>
                                                    <TableCell style={NUM}>{money(x.Delivery)}</TableCell>
                                                    <TableCell style={NUM}>{money(x.NDR)}</TableCell>
                                                    <TableCell style={NUM}>
                                                        {(x.otherLines || []).length > 0
                                                            ? <button type="button" style={LINK_BUTTON} className="pc-link-button"
                                                                aria-label={`Show other expenses for ${x.ShopName}`}
                                                                onClick={() => this.setState({ otherFor: x })}>{money(x.other)}</button>
                                                            : money(x.other)}
                                                    </TableCell>
                                                    <TableCell style={NUM}>{money(x.TotalExp)}</TableCell>
                                                    <TableCell style={{ ...NUM, fontWeight: 700 }}>{money(x.ProfnLoss)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                        <TableFooter>
                                            {/* As the legacy file: only Cash and Card are totalled. */}
                                            <TableRow>
                                                <TableCell style={FOOT}>Total</TableCell>
                                                <TableCell style={{ ...FOOT, ...NUM }}>{money(sumMoney(rows, 'Cash'))}</TableCell>
                                                <TableCell style={{ ...FOOT, ...NUM }}>{money(sumMoney(rows, 'Card'))}</TableCell>
                                                {Array.from({ length: 9 }, (_, i) => <TableCell key={i} style={FOOT} />)}
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Col>
                    </Row>
                )}
                {this.renderOtherModal()}
            </section>
        );
    }
}

export default Summary;
